import SearchBox from 'components/SearchBox'
import Translation from 'components/Translation'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useEffect, useRef } from 'react'
import { useState } from 'react'
import { classNames } from 'utils/dom-helpers'
import { useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { ModalDataAttributes } from '../types'
import StaffCardItem, { StaffCardItemProps } from './StaffCardItem'
import StaffListItem, { StaffListItemProps } from './StaffListItem'

function ServiceModalStaff() {
    const { ServiceModal } = useAuth()
    const { attributes, setAttributes } = ServiceModal
    const { staff } = useTenantContext()
    const api_error =
        attributes && (attributes.api_error as Record<string, string>)
    const [mapped_staff, setMappedStaff] = useState<any[]>([])
    const [filtered_staff, setFilteredStaffList] = useState<Record<string, string>[]>([])
    const [selected_staff, selectStaff] = useState<number>()
    const [filter, setFilter] = useState<string>("")
    const [show_dropdown, toggleShowDropdown] = useState<boolean>(false)
    const ref = useRef<HTMLDivElement>(null)

    let timeout: NodeJS.Timeout | number = 0


    const { //TODO: move this to tenant context and implement proper caching and recaching when adding/removing availabilities in the staff modal
        data: staff_with_availability_xhr,
        doFetch: getStaffWithAvailability,
    } = useFetch(
        `/v1/staff/availability/by-service?serviceId=${attributes?.id}&size=500`,
        FetchMethods.GET,
        !!attributes?.id
    )

    async function searchEmployee() {
        if (mapped_staff) {
            const filter_lower = filter.toLocaleLowerCase()
            setFilteredStaffList(
                mapped_staff.filter((result) => {
                    return result.first_name.toLocaleLowerCase().includes(filter_lower)
                        || result.last_name.toLocaleLowerCase().includes(filter_lower)
                })
            )
        }
    }

    useEffect(() => {
        if (attributes?.refetch && attributes?.id) {
            getStaffWithAvailability()
            setAttributes({
                ...attributes,
                refetch: false
            })
        }
    }, [attributes?.refetch])

    useEffect(() => {
        if (
            selected_staff != undefined &&
            filtered_staff?.length > 0 &&
            filtered_staff[selected_staff]
        ) {
            toggleShowDropdown(false)
            let { staff_assigned } = attributes as unknown as Record<
                string,
                Record<string, string>[]
            >
            if (!staff_assigned) {
                staff_assigned = [filtered_staff[selected_staff]]
            } else if (
                staff_assigned.filter(({ id }) => {
                    return id == filtered_staff[selected_staff].id
                }).length == 0
            ) {
                staff_assigned.push(filtered_staff[selected_staff])
            }
            filtered_staff.splice(selected_staff, 1)
            setAttributes({
                ...attributes,
                staff_assigned,
            })
            selectStaff(undefined)
        }
    }, [selected_staff])

    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                toggleShowDropdown(false)
            }
        }
        document.addEventListener('click', handleClickOutside, true)
        return () => {
            document.removeEventListener('click', handleClickOutside, true)
        }
    }, [ref])

    function removeStaff(idx: number) {
        console.log("Delete Staff", idx);
        
        const staff_assigned =
            attributes &&
            (attributes.staff_assigned as unknown as Record<
                string,
                string
            >[])
        if (staff_assigned) {
            staff_assigned.splice(idx, 1)
            setAttributes({
                ...attributes,
                staff_assigned,
            })
        }
    }

    useEffect(() => {
        if (!filter || filter.length == 0) {
            setFilteredStaffList(mapped_staff as Record<string, string>[])
        } else if (staff && staff.length) {
            if (timeout != 0) {
                clearTimeout(timeout as NodeJS.Timeout)
            }
            timeout = setTimeout(() => {
                searchEmployee()
            }, 300)
        }
    }, [filter, mapped_staff])

    useEffect(() => {

        if (staff) {
            let mapped = staff.map((sta) => {
                const { id } = sta
                const {
                    image_url: staff_image,
                    id: staff_user_id,
                    first_name: first_name,
                    last_name: last_name,
                } = sta.user as Record<string, string>
                return {
                    id,
                    staff_image,
                    staff_user_id,
                    first_name,
                    last_name
                }
            })
            let existing: StaffCardItemProps[] = []
            if (staff_with_availability_xhr.length > 0) {
                mapped = mapped.filter((s) => {
                    if ((staff_with_availability_xhr as any[]).some((s_a) => s.id == s_a.staff.id)) {
                        existing.push({
                            ...s,
                            has_existing_availability: true
                        })
                        return false
                    } else {
                        return !(attributes?.staff_assigned && (attributes?.staff_assigned as Record<string, string>[])
                            .some((s_a) => s.id == s_a.id))
                    }
                }).sort((a, b) => {
                    return (a.first_name + a.last_name).localeCompare(b.first_name + b.last_name)
                })
            }
            setMappedStaff(mapped)
            if (existing.length > 0) {
                let assigned: StaffCardItemProps[] = []
                if (attributes?.staff_assigned && (attributes?.staff_assigned as Record<string, string>[]).length > 0) {
                    (attributes?.staff_assigned as Record<string, string>[]).forEach((sta) => {
                        if (!existing.some((e) => e.id == sta.id)) {
                            assigned.push({
                                id: sta.id,
                                staff_image: sta.staff_image,
                                staff_user_id: sta.staff_user_id,
                                first_name: sta.first_name,
                                last_name: sta.last_name,
                                has_existing_availability: false
                            })
                        }
                    })
                }

                setAttributes({
                    ...attributes,
                    staff_assigned: existing.concat(assigned)
                })
            }

        }
    }, [staff, (attributes?.staff_assigned as ModalDataAttributes[])?.length, staff_with_availability_xhr])

    const is_staff_assigned =
        attributes &&
        attributes.staff_assigned &&
        (attributes.staff_assigned as unknown as StaffCardItemProps[])
            .length > 0

    return (
        <div
            className="relative flex flex-col w-full"
        >
            <div className="text-primary text-xl font-medium font-display">
                <Translation
                    content_key="common.assign_staff_members"
                />
                <div className="text-base text-gray-900 mt-2">
                    <Translation
                        content_key="common.assign_staff_members_for_service"
                    />
                </div>
            </div>
            <div
                className={classNames(
                    is_staff_assigned ? '' : '',
                    'text-center p-5 rounded-xl bg-gray-100 mt-3 relative flex-1'
                )}
            >
                <div ref={ref} className="flex mb-3 mx-3">
                    <SearchBox
                        value={filter}
                        setValue={setFilter}
                        onClick={() => { toggleShowDropdown(!show_dropdown) }}
                    />
                    <button
                        type="button"
                        className="text-primary-light text-center w-32"
                        onClick={() => {
                            setAttributes({
                                ...attributes,
                                staff_assigned: [],
                            })
                        }}
                    >
                        <Translation
                            content_key='remove_all'
                        />
                    </button>
                </div>
                {show_dropdown && filtered_staff?.length > 0 ? (
                    <div ref={ref} className="absolute left-6 bg-white rounded-lg shadow-xl max-h-80 overflow-auto">
                        {(filtered_staff as unknown as StaffListItemProps[]).map(
                            (s: StaffListItemProps, idx) => (
                                <StaffListItem
                                    {...s}
                                    key={s.id}
                                    onClick={() => {
                                        selectStaff(idx)
                                    }}
                                    selectItem={() => {
                                        selectStaff(idx)
                                    }}
                                    is_selected={idx === selected_staff}
                                />
                            )
                        )}
                    </div>
                ) : (
                    ''
                )}
                <div
                    className={classNames(
                        is_staff_assigned
                            ? 'justify-start'
                            : 'justify-center',
                        'overflow-auto flex-1 flex flex-col p-3'
                    )}
                >
                    {is_staff_assigned ? (
                        <div className="grid gap-6 grid-cols-2">
                            {(
                                attributes.staff_assigned as unknown as StaffCardItemProps[]
                            ).map((s, idx) => {
                                return (
                                    <StaffCardItem
                                        key={s.id}
                                        {...s}
                                        has_existing_availability={(s?.has_existing_availability as boolean)}
                                        onDelete={() => {
                                            removeStaff(idx)
                                        }}
                                    />
                                )
                            })}
                        </div>
                    ) : (
                        <>
                            <div className="rounded-full bg-gray-200 w-20 h-20 inline-block leading-loose text-center self-center align-middle">
                                <i className="text-gray-400 feather feather-user text-4xl leading-loose" />
                            </div>
                            <div className="text-gray-400 block mt-4 mx-auto">
                                <Translation
                                    content_key="common.search_staff_member"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div>
                {api_error && api_error.message ? (
                    <div className="text-sm text-red-700">
                        {api_error.message}
                    </div>
                ) : (
                    ''
                )}
            </div>
            {/* <div>
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg py-3 inline-block mr-3 px-10"
                        onClick={() => {
                            onPrevious()
                        }}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onNext()
                        }}
                        className={classNames(
                            'group relative flex justify-center',
                            'py-3 px-12 border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            loading
                                ? 'bg-primary-light'
                                : api_error && api_error.message
                                ? 'bg-red-700 hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                    >
                        Next
                    </button>
                </div>
            </div> */}
        </div>
    )
}

export default ServiceModalStaff
